import React from 'react';

import { FeatureAccessLevel, useFeatureFlags, invalidateFeatureFlags } from '../../hooks/useFeatureEnabled';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface FeatureGateProps {
  featureCode: string;
  requiredLevel?: FeatureAccessLevel;
  /**
   * Render prop for gated content. Prefer providing a function to avoid rendering
   * gated components before access is confirmed.
   */
  children: React.ReactNode | (() => React.ReactNode);
  fallback?: React.ReactNode;
  loadingFallback?: React.ReactNode;
}

const DefaultFallback: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6 text-center space-y-2">
    <h2 className="text-lg font-semibold text-gray-900">Feature Unavailable</h2>
    <p className="text-sm text-gray-600">
      This feature is currently disabled for your account. Please contact a superuser if you need access.
    </p>
  </div>
);

// Shown when /my-features/ came back 401 — i.e. the session is (probably) gone.
// Background polls deliberately never force a logout, so nothing else will move
// the user off this page; give them an explicit way out instead of a dead end.
const AuthFallback: React.FC = () => {
  const signIn = () => {
    const currentPath = window.location.pathname + window.location.search;
    const loginPath = currentPath.startsWith('/onecare') ? '/onecare/login' : '/login';
    // Full page load, not a router navigation: the session is dead, so a clean
    // boot (fresh rehydrate, no stale in-memory auth state) is what we want.
    window.location.assign(`${loginPath}?redirect=${encodeURIComponent(currentPath)}`);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 text-center space-y-3 max-w-md mx-auto mt-8">
      <h2 className="text-lg font-semibold text-gray-900">Your session has expired</h2>
      <p className="text-sm text-gray-600">
        We couldn’t verify your access to this page because your session is no longer
        valid. Please sign in again to continue.
      </p>
      <button
        type="button"
        onClick={signIn}
        className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Sign in again
      </button>
    </div>
  );
};

const LoadErrorFallback: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6 text-center space-y-3 max-w-md mx-auto mt-8">
    <h2 className="text-lg font-semibold text-gray-900">Couldn’t load this page</h2>
    <p className="text-sm text-gray-600">
      We couldn’t reach the server to check your access. This is usually a brief
      connection hiccup.
    </p>
    <button
      type="button"
      onClick={() => invalidateFeatureFlags()}
      className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      Retry
    </button>
  </div>
);

const FeatureGate: React.FC<FeatureGateProps> = ({
  featureCode,
  requiredLevel = 'standard',
  children,
  fallback,
  loadingFallback,
}) => {
  const { isLoading, hasFeature, hasAuthFailure, hasLoadError } = useFeatureFlags();

  if (isLoading) {
    return (
      <>
        {loadingFallback ?? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="md" />
          </div>
        )}
      </>
    );
  }

  // A non-401 feature-load failure (server/network blip) — show a retry instead
  // of spinning forever.
  if (hasLoadError) {
    return <LoadErrorFallback />;
  }

  if (hasAuthFailure) {
    return <AuthFallback />;
  }

  if (!hasFeature(featureCode, requiredLevel)) {
    return <>{fallback ?? <DefaultFallback />}</>;
  }

  const renderChildren =
    typeof children === 'function' ? (children as () => React.ReactNode) : () => children;

  return <>{renderChildren()}</>;
};

export default FeatureGate;

